/**
 * Cover picker — F 201 D.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * F 200 put a working cover editor in the card panel, and it worked: the
 * strip plus a 12-swatch gradient grid plus "Remove cover". The operator's
 * verdict on that was "this is not world class" — and they are right, though
 * not for the reason the dispatch assumed. The problem was never that the
 * gradients were wrong. It is that the PICKER WAS PERMANENT. Twelve 26px
 * swatches and a Remove button sat under every card, taking vertical space
 * and shouting at a user who opened a card to read it. A control that is
 * always visible stops being a control.
 *
 * So: the strip stays, the grid goes, and the picker opens on demand behind
 * one "Change cover" affordance that appears on hover or keyboard focus
 * (D.1, D.2).
 *
 * WHY THERE IS NO UNSPLASH TAB
 * ----------------------------
 * The dispatch asks for five tabs and adds: "If no Unsplash API key exists
 * in the sandbox or kit, STOP on D.5 and report — do NOT invent a key or a
 * proxy." I checked before building:
 *
 *   grep -ri unsplash src/     -> 0 hits, no integration of any kind
 *   env | grep -i unsplash     -> nothing
 *   kit env file               -> nothing
 *
 * There is no key, and D.5 says do not invent one. Rather than ship four tabs
 * while silently dropping the fifth, the tab is PRESENT and visibly
 * disabled, with the reason stated in the UI: "Unsplash — no API key
 * configured". An operator sees the gap and knows it is unbuilt; a missing
 * tab reads as an oversight. Wiring the tab to the real service is one prop
 * away once a key exists.
 *
 * THE COVER STILL PERSISTS INTO properties['cover'] (D.4). No new storage
 * channel was introduced, and resolveCover() — which checks that property
 * BEFORE its deterministic id-hash fallback — is unchanged. This component
 * is handed a `onPick` callback and knows nothing about storage.
 */
import React, { useRef, useState } from 'react';
import { COVER_GRADIENTS } from '../database/coverResolver';
import { Modal } from '../../primitives/Modal';

/** Solid colours for the Colours tab. Deliberately NOT the gradients. */
export const COVER_COLOURS: string[] = [
  '#0f172a', '#334155', '#64748b', '#94a3b8', '#cbd5e1', '#f1f5f9',
  '#fee2e2', '#fecaca', '#fca5a5', '#ef4444', '#dc2626', '#991b1b',
  '#dcfce7', '#bbf7d0', '#86efac', '#22c55e', '#16a34a', '#166534',
  '#fef3c7', '#fde68a', '#fcd34d', '#f59e0b', '#d97706', '#92400e',
  '#dbeafe', '#bfdbfe', '#93c5fd', '#3b82f6', '#2563eb', '#1e40af',
  '#ede9fe', '#ddd6fe', '#c4b5fd', '#8b5cf6', '#7c3aed', '#5b21b6',
];

export type CoverSource =
  | { kind: 'gradient'; value: string }
  | { kind: 'colour'; value: string }
  | { kind: 'url'; value: string }
  | { kind: 'file'; dataUrl: string; name: string };

type Tab = 'gradients' | 'colours' | 'unsplash' | 'upload' | 'link';

const TABS: { id: Tab; label: string }[] = [
  { id: 'gradients', label: 'Gradients' },
  { id: 'colours', label: 'Colours' },
  { id: 'unsplash', label: 'Unsplash' },
  { id: 'upload', label: 'Upload' },
  { id: 'link', label: 'Link' },
];

export interface CoverPickerModalProps {
  opened: boolean;
  onClose: () => void;
  /** The card's current cover, shown in the preview. */
  current: string | null;
  onPick: (source: CoverSource | null) => void;
}

export const CoverPickerModal: React.FC<CoverPickerModalProps> = ({
  opened,
  onClose,
  current,
  onPick,
}) => {
  const [tab, setTab] = useState<Tab>('gradients');
  const [link, setLink] = useState('');
  const fileRef = useRef<HTMLInputElement | null>(null);

  const pick = (source: CoverSource | null) => {
    onPick(source);
    onClose();
  };

  const onFile = (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    // F 200's PageEditor already rejects images over 4MB; match it so the two
    // image paths fail the same way rather than one silently succeeding.
    if (file.size > 4 * 1024 * 1024) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result ?? '');
      if (dataUrl) pick({ kind: 'file', dataUrl, name: file.name });
    };
    reader.readAsDataURL(file);
  };

  const swatchGrid = (values: string[], kind: 'gradient' | 'colour') => (
    <div
      data-fc-cover-grid={kind}
      style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10 }}
    >
      {values.map((v, i) => (
        <button
          key={i}
          type="button"
          data-fc-cover-swatch={i}
          aria-label={`${kind} ${i + 1}`}
          onClick={() => pick({ kind, value: v })}
          style={{
            height: 52, borderRadius: 8, cursor: 'pointer',
            background: v,
            border: current === v ? '2px solid var(--fc-accent)' : '1px solid rgba(0,0,0,0.12)',
          }}
        />
      ))}
    </div>
  );

  return (
    <Modal opened={opened} onClose={onClose} title="Change cover">
      <div data-fc-cover-picker style={{ minWidth: 420 }}>
        {/* D.3 — preview at the top */}
        <div
          data-fc-cover-preview
          style={{
            height: 96, borderRadius: 10, marginBottom: 14,
            background: current ?? 'var(--fc-bg-tertiary)',
            backgroundSize: 'cover', backgroundPosition: 'center',
          }}
        />

        {/* D.3 — tab row */}
        <div role="tablist" style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              data-fc-cover-tab={t.id}
              disabled={t.id === 'unsplash'}
              title={t.id === 'unsplash' ? 'No Unsplash API key configured' : undefined}
              onClick={() => setTab(t.id)}
              style={{
                padding: '6px 12px', borderRadius: 6, cursor: t.id === 'unsplash' ? 'not-allowed' : 'pointer',
                border: '1px solid var(--fc-border-primary)',
                background: tab === t.id ? 'var(--fc-accent)' : 'transparent',
                color: tab === t.id ? '#fff' : 'var(--fc-text-primary)',
                opacity: t.id === 'unsplash' ? 0.45 : 1,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* D.3 — tab bodies */}
        {tab === 'gradients' && swatchGrid(COVER_GRADIENTS, 'gradient')}
        {tab === 'colours' && swatchGrid(COVER_COLOURS, 'colour')}

        {tab === 'unsplash' && (
          <p data-fc-cover-unsplash-note style={{ fontSize: 13, opacity: 0.75 }}>
            Unsplash needs an API key, and none is configured in this
            deployment. The tab is disabled rather than quietly removed so the
            gap is visible.
          </p>
        )}

        {tab === 'upload' && (
          <div data-fc-cover-upload>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={(e) => onFile(e.target.files?.[0])}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              style={{
                padding: '18px 22px', borderRadius: 8, cursor: 'pointer', width: '100%',
                border: '1px dashed var(--fc-border-primary)', background: 'transparent',
                color: 'var(--fc-text-primary)',
              }}
            >
              Choose an image… (max 4MB)
            </button>
          </div>
        )}

        {tab === 'link' && (
          <div data-fc-cover-link style={{ display: 'flex', gap: 8 }}>
            <input
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://…/image.jpg"
              style={{
                flex: 1, padding: '8px 10px', borderRadius: 6,
                border: '1px solid var(--fc-border-primary)',
                background: 'var(--fc-bg-primary)', color: 'var(--fc-text-primary)',
              }}
            />
            <button
              type="button"
              disabled={!link.trim()}
              onClick={() => pick({ kind: 'url', value: link.trim() })}
              style={{
                padding: '8px 16px', borderRadius: 6, cursor: link.trim() ? 'pointer' : 'not-allowed',
                border: '1px solid var(--fc-border-primary)', background: 'var(--fc-accent)', color: '#fff',
              }}
            >
              Use
            </button>
          </div>
        )}

        {/* D.3 — Remove at the bottom-left */}
        <div style={{ display: 'flex', marginTop: 18 }}>
          <button
            type="button"
            data-fc-cover-remove
            onClick={() => pick(null)}
            style={{
              padding: '7px 13px', borderRadius: 6, cursor: 'pointer',
              border: '1px solid var(--fc-border-primary)', background: 'transparent',
              color: 'var(--fc-text-secondary)',
            }}
          >
            Remove cover
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CoverPickerModal;